import { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserCallAudio } from '../lib/callAudio';
import { callRequest, streamReply, takeSpeechChunk } from '../lib/callApi';
import { API } from '../lib/site';

export const LANGUAGES = [
  { code: 'en', label: 'English', locale: 'en-US' },
  { code: 'es', label: 'Español', locale: 'es-US' },
  { code: 'vi', label: 'Tiếng Việt', locale: 'vi-VN' },
  { code: 'ko', label: '한국어', locale: 'ko-KR' },
];
const localeFor = code => LANGUAGES.find(item => item.code === code)?.locale || 'en-US';

const blank = { language: 'en', languageChosen: false, urgent: false, urgentReason: '', urgentPhone: '', smsStatus: 'pending', active: false, started: false, ended: false, microphone: false, muted: false, speaking: false, generating: false, voicePreparing: false, voiceEnabled: true, micError: false, error: '', draft: '', messages: [], duration: 0, saveStatus: 'idle', summaryStatus: 'pending', summary: '', emailStatus: 'not_configured', saveWarning: '', intake: {}, intakeRevision: 0, intakeError: false, intakeSaving: false, contactSaved: false, callbackReference: '', appointment: null };

export const useLiveCall = () => {
  const [state, setState] = useState(blank);
  const mounted = useRef(true);
  const control = useRef({ active: false, messages: [], version: 0, token: 0 });
  const audio = useRef(null);
  const actions = useRef({});
  const notify = useCallback(patch => { if (mounted.current) setState(s => ({ ...s, ...patch })); }, []);
  const publish = useCallback(() => notify({ messages: control.current.messages.map(message => ({ ...message })) }), [notify]);
  const snapshot = useCallback(() => {
    const c = control.current;
    return { version: ++c.version, duration: Math.min(3600, Math.floor((Date.now() - c.startedAt) / 1000)), transcript: c.messages.map(message => ({ ...message })) };
  }, []);

  const applyIntake = useCallback((result, id) => {
    const c = control.current;
    if (c.id !== id || !result?.intake || (result.intake_revision || 0) < (c.intakeRevision || 0)) return;
    c.intakeRevision = result.intake_revision || 0;
    c.contactSaved = !!result.contact_saved;
    c.intakePending = false;
    notify({ intake: result.intake, intakeRevision: c.intakeRevision, intakeError: !!result.intake_error,
      intakeSaving: false, contactSaved: c.contactSaved, callbackReference: result.callback_reference || '' });
  }, [notify]);


  const checkpoint = useCallback(async () => {
    const c = control.current;
    if (!c.active || !c.id) return;
    const id = c.id; const body = snapshot();
    try {
      await c.startPromise;
      await callRequest(`/calls/${id}/checkpoint`, body);
      if (control.current.id === id) notify({ saveWarning: '' });
    } catch { if (control.current.id === id) notify({ saveWarning: 'Transcript sync paused. We’ll retry while the call is open.' }); }
  }, [snapshot, notify]);

  const interrupt = useCallback(() => {
    const c = control.current;
    if (!c.generating && !c.speaking && !c.voicePreparing && !c.intakePending) return;
    const hadReplyWork = c.generating || c.speaking || c.voicePreparing;
    c.token += 1;
    c.generating = false; c.speaking = false; c.voicePreparing = false; c.intakePending = false;
    c.controller?.abort(); clearTimeout(c.requestTimer);
    const reply = c.messages.find(message => message.id === c.assistantId);
    if (reply && hadReplyWork) reply.interrupted = true;
    c.messages = c.messages.filter(message => message.content);
    c.assistantId = null;
    audio.current?.cancelReply();
    notify({ generating: false, speaking: false, voicePreparing: false, intakeSaving: false }); publish();
    checkpoint();
  }, [checkpoint, notify, publish]);

  const send = useCallback(async text => {
    const c = control.current;
    const message = text.trim().slice(0, 2000);
    if (!c.active || !message) return;
    if (c.messages.length >= 190) { actions.current.end('time_limit'); return; }
    interrupt();
    audio.current?.discardDraft();
    const conversation = c.messages.slice(-16).map(item => ({ role: item.role, content: item.role === 'assistant' && item.delivery === 'voice' ? item.spoken_text : item.content })).filter(item => item.content);
    const at = Math.floor((Date.now() - c.startedAt) / 1000);
    const userId = crypto.randomUUID();
    const assistantId = crypto.randomUUID();
    const token = ++c.token;
    c.assistantId = assistantId; c.generating = true; c.intakePending = true; c.lastActivity = Date.now(); c.draft = '';
    c.messages.push({ id: userId, role: 'user', content: message, spoken_text: '', interrupted: false, delivery: 'text', at });
    c.messages.push({ id: assistantId, role: 'assistant', content: '', spoken_text: '', interrupted: false, delivery: c.voiceEnabled ? 'voice' : 'text', at });
    c.controller = new AbortController();
    const controller = c.controller;
    notify({ generating: true, intakeSaving: true, draft: '', error: c.micError ? c.micMessage : '' }); publish();
    audio.current?.beginReply(); checkpoint();
    let speechBuffer = ''; let firstSpeechChunk = true;
    c.requestTimer = setTimeout(() => controller.abort(), 85000);
    try {
      await c.startPromise;
      if (!c.active || token !== c.token) return;
      await streamReply({ session_id: c.id, message_id: userId, message, consent: true, call_mode: true, conversation, language: c.languageChosen ? c.language : null }, controller.signal, delta => {
        if (!c.active || token !== c.token) return;
        const reply = c.messages.find(item => item.id === assistantId);
        if (!reply) return;
        reply.content = (reply.content + delta).slice(0, 6000);
        speechBuffer += delta;
        if (c.voiceEnabled) {
          let chunk;
          do {
            [chunk, speechBuffer] = takeSpeechChunk(speechBuffer, false, firstSpeechChunk);
            if (chunk) { audio.current?.enqueue(chunk); firstSpeechChunk = false; }
          } while (chunk);
        }
        publish();
      }, doneEvent => {
        if (!c.active || token !== c.token) return;
        if (c.voiceEnabled && speechBuffer.trim()) audio.current?.enqueue(speechBuffer);
        speechBuffer = ''; c.generating = false;
        notify({ generating: false, ...(doneEvent?.appointment ? { appointment: doneEvent.appointment } : {}) });
      },
      event => { if (c.active && token === c.token) applyIntake(event, c.id); },
      event => {
        if (!c.active || token !== c.token) return;
        if (event.type === 'urgent') notify({ urgent: true, urgentReason: event.reason, urgentPhone: event.phone || '' });
        if (event.type === 'language' && event.language !== c.language) { c.language = event.language; audio.current?.setLanguage(localeFor(event.language)); notify({ language: event.language }); }
      });
      if (!c.active || token !== c.token) return;
      if (c.voiceEnabled && speechBuffer.trim()) audio.current?.enqueue(speechBuffer);
    } catch (error) {
      if (!c.active || token !== c.token) return;
      const reply = c.messages.find(item => item.id === assistantId);
      if (reply) reply.interrupted = true;
      audio.current?.cancelReply();
      c.messages = c.messages.filter(item => item.content);
      notify({ error: error.name === 'AbortError' ? 'The reply took too long. Please ask again or call the firm directly.' : (error.message === 'Failed to fetch' ? 'The connection dropped. Please try speaking again or use the phone link.' : error.message) }); publish();
    } finally {
      if (token === c.token) {
        clearTimeout(c.requestTimer); c.generating = false; c.intakePending = false;
        notify({ generating: false, intakeSaving: false }); checkpoint();
      }
    }
  }, [checkpoint, interrupt, notify, publish, applyIntake]);

  const pollSummary = useCallback(async id => {
    let latest;
    for (let attempt = 0; attempt < 30; attempt++) {
      if (!mounted.current || control.current.id !== id) return;
      try {
        const result = await callRequest(`/calls/${id}/result`);
        if (control.current.id !== id) return;
        latest = result;
        applyIntake(result, id);
        notify({ summaryStatus: result.summary_status, summary: result.summary, emailStatus: result.email_status, smsStatus: result.sms_status, urgent: !!result.urgent, urgentReason: result.urgent_reason || '' });
        if (result.summary_status !== 'processing' && !['pending', 'sending', 'retrying'].includes(result.email_status) && !['pending', 'sending'].includes(result.sms_status)) return;
      } catch { /* A transient polling failure is retried; the saved transcript remains intact. */ }
      await new Promise(resolve => setTimeout(resolve, 3000));
    }
    if (control.current.id === id && (!latest || latest.summary_status === 'processing')) notify({ summaryStatus: 'unavailable' });
  }, [notify, applyIntake]);

  const saveEnd = useCallback(async (id, body, startPromise) => {
    if (control.current.id === id) notify({ saveStatus: 'saving' });
    try {
      await startPromise;
      const result = await callRequest(`/calls/${id}/end`, body);
      if (control.current.id !== id) return;
      applyIntake(result, id);
      notify({ saveStatus: 'saved', summaryStatus: result.summary_status, summary: result.summary, emailStatus: result.email_status, smsStatus: result.sms_status, urgent: !!result.urgent, urgentReason: result.urgent_reason || '', saveWarning: '' });
      if (result.summary_status === 'processing' || ['pending', 'sending', 'retrying'].includes(result.email_status) || ['pending', 'sending'].includes(result.sms_status)) pollSummary(id);
    } catch {
      if (control.current.id === id) notify({ saveStatus: 'error', saveWarning: 'We couldn’t save the completed call. Your transcript is still visible here. Please retry.' });
    }
  }, [notify, pollSummary, applyIntake]);

  const end = useCallback((reason = 'user') => {
    const c = control.current;
    if (!c.active) return;
    interrupt();
    if (c.draft?.trim()) c.messages.push({ id: crypto.randomUUID(), role: 'user', content: c.draft.trim().slice(0, 2000), spoken_text: '', delivery: 'voice', interrupted: true, at: Math.floor((Date.now() - c.startedAt) / 1000) });
    c.active = false; c.token += 1; c.draft = '';
    clearTimeout(c.requestTimer); c.controller?.abort(); audio.current?.stop();
    const body = { ...snapshot(), reason };
    c.endBody = body;
    notify({ active: false, ended: true, microphone: false, speaking: false, generating: false, voicePreparing: false, draft: '', duration: body.duration, error: '' }); publish();
    saveEnd(c.id, body, c.startPromise);
  }, [interrupt, notify, publish, saveEnd, snapshot]);

  const start = useCallback(() => {
    if (control.current.active) return;
    audio.current?.stop();
    const previous = control.current;
    const c = { id: crypto.randomUUID(), active: true, messages: [], version: 0, token: 0, startedAt: Date.now(), lastActivity: Date.now(), voiceEnabled: true, muted: false, micError: false, generating: false, speaking: false, voicePreparing: false, draft: '', language: previous.languageChosen ? previous.language : 'en', languageChosen: !!previous.languageChosen };
    control.current = c;
    audio.current?.setLanguage(localeFor(c.language));
    notify({ ...blank, active: true, started: true, language: c.language, languageChosen: c.languageChosen });
    c.startPromise = callRequest('/calls/start', { call_id: c.id, consent: true, transcript_email_consent: true, spoken_intake_consent: true });
    c.startPromise.then(result => {
      if (control.current.id !== c.id || !c.active || c.messages.length || c.draft) return;
      const text = result.welcome_text;
      if (!text) return;
      c.assistantId = crypto.randomUUID();
      c.messages.push({ id: c.assistantId, role: 'assistant', content: text, spoken_text: '',
        interrupted: false, delivery: c.voiceEnabled ? 'voice' : 'text', at: 0 });
      publish();
      if (c.voiceEnabled) { audio.current?.beginReply(); audio.current?.enqueue(text); }
      checkpoint();
    }).catch(() => { if (control.current.id === c.id) notify({ saveWarning: 'The call could not connect to the server. Check your connection and start a new call.' }); });
    audio.current?.start();
  }, [checkpoint, notify, publish]);

  const setLanguage = useCallback(code => {
    const c = control.current;
    c.language = code; c.languageChosen = true;
    audio.current?.setLanguage(localeFor(code));
    notify({ language: code, languageChosen: true });
  }, [notify]);

  const touch = useCallback(() => { if (control.current.active) control.current.lastActivity = Date.now(); }, []);

  const saveContact = useCallback(async values => {
    const c = control.current;
    if (!c.id) throw new Error('Please start a new call before saving contact details.');
    const id = c.id;
    touch();
    await c.startPromise;
    const result = await callRequest(`/calls/${id}/contact`, values);
    if (control.current.id === id) {
      c.contactSaved = true;
      c.intakeRevision = result.intake_revision;
      setState(current => ({ ...current, contactSaved: true, callbackReference: result.reference, intakeRevision: result.intake_revision,
        intake: { ...current.intake, ...values, phone_confirmed: true, email_confirmed: true, contact_consent: true } }));
    }
    return result;
  }, [touch]);

  const toggleMute = useCallback(() => {
    const c = control.current;
    if (!c.active) return;
    c.muted = !c.muted; c.draft = '';
    audio.current?.setMuted(c.muted);
    notify({ muted: c.muted, draft: '' });
  }, [notify]);

  const retryMicrophone = useCallback(() => {
    const c = control.current;
    if (!c.active) return;
    c.muted = false; c.micError = false; c.voiceEnabled = true; c.micMessage = '';
    notify({ micError: false, error: '', muted: false, voiceEnabled: true });
    audio.current?.unlockVoice();
    audio.current?.setMuted(false);
  }, [notify]);

  const useText = useCallback(() => {
    const c = control.current;
    interrupt(); c.voiceEnabled = false; c.muted = true; c.draft = '';
    audio.current?.setMuted(true);
    notify({ voiceEnabled: false, muted: true, error: '', draft: '' });
  }, [interrupt, notify]);

  const retrySave = useCallback(() => {
    const c = control.current;
    if (c.endBody) {
      c.startPromise = callRequest('/calls/start', { call_id: c.id, consent: true, transcript_email_consent: true, spoken_intake_consent: true });
      saveEnd(c.id, c.endBody, c.startPromise);
    }
  }, [saveEnd]);

  const clear = useCallback(async () => {
    const c = control.current;
    if (c.active) return;
    try {
      const response = await fetch(`${API}/chat/${c.id}`, { method: 'DELETE', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error();
      c.messages = []; c.id = null;
      notify({ messages: [], summary: '', summaryStatus: 'pending', saveStatus: 'deleted', saveWarning: '', intake: {}, intakeError: false, intakeSaving: false, contactSaved: false, callbackReference: '' });
    } catch { notify({ saveWarning: 'The transcript could not be deleted. Please try again.' }); }
  }, [notify]);

  actions.current = { send, interrupt, end, checkpoint };
  useEffect(() => {
    mounted.current = true;
    audio.current = new BrowserCallAudio({
      getCallId: () => control.current.id,
      isReplying: () => control.current.generating || control.current.speaking || control.current.voicePreparing,
      onPreparing: voicePreparing => { control.current.voicePreparing = voicePreparing; notify({ voicePreparing }); },
      onInterrupt: () => actions.current.interrupt(),
      onFinal: text => actions.current.send(text),
      onDraft: draft => { control.current.draft = draft; control.current.lastActivity = Date.now(); notify({ draft }); },
      onMicrophone: microphone => {
        const c = control.current;
        if (microphone && c.micError) { c.micError = false; c.micMessage = ''; notify({ microphone, micError: false, error: '' }); }
        else notify({ microphone });
      },
      onSpeaking: speaking => {
        const c = control.current; c.speaking = speaking;
        if (!speaking) c.lastActivity = Date.now();
        notify({ speaking });
        if (!speaking && c.active && !c.generating) actions.current.checkpoint();
      },
      onSpokenProgress: text => {
        const c = control.current;
        const message = c.messages.find(item => item.id === c.assistantId);
        if (message && !message.interrupted) { message.spoken_text = text.slice(0, 6000); publish(); }
      },
      onError: (error, kind) => {
        const c = control.current;
        if (kind === 'microphone') { c.micError = true; c.micMessage = error; notify({ error, micError: true, microphone: false }); }
        else {
          c.voiceEnabled = false;
          const message = c.messages.find(item => item.id === c.assistantId);
          if (message) message.delivery = 'text';
          notify({ error, voiceEnabled: false }); publish();
        }
      },
    });
    const clock = setInterval(() => {
      const c = control.current;
      if (!c.active) return;
      const duration = Math.floor((Date.now() - c.startedAt) / 1000);
      notify({ duration });
      if (duration >= 900) actions.current.end('time_limit');
      else if (!c.generating && !c.speaking && !c.voicePreparing && Date.now() - c.lastActivity >= 120000) actions.current.end('inactivity');
    }, 1000);
    const heartbeat = setInterval(() => actions.current.checkpoint(), 25000);
    const pageLeaving = () => {
      const c = control.current;
      if (!c.active || !c.id) return;
      actions.current.interrupt();
      c.active = false; audio.current?.stop(); c.controller?.abort();
      const content = JSON.stringify({ ...snapshot(), reason: 'page_left' });
      const payload = new Blob([content], { type: 'application/json' });
      const url = `${API}/calls/${c.id}/${payload.size < 60000 ? 'end' : 'disconnect'}`;
      const body = payload.size < 60000 ? payload : new Blob(['{}'], { type: 'application/json' });
      if (!navigator.sendBeacon?.(url, body)) fetch(url, { method: 'POST', body, keepalive: true }).catch(() => {});
    };
    window.addEventListener('pagehide', pageLeaving);
    return () => {
      pageLeaving(); mounted.current = false;
      clearInterval(clock); clearInterval(heartbeat);
      window.removeEventListener('pagehide', pageLeaving);
      audio.current?.stop(); clearTimeout(control.current.requestTimer);
    };
  }, [notify, publish, snapshot]);

  return { ...state, start, send, end, toggleMute, retryMicrophone, useText, retrySave, clear, saveContact, touch, setLanguage };
};